import React, { useState } from 'react';
import { MdClose, MdPerson, MdPhone, MdEmail, MdBusiness, MdLocationOn } from 'react-icons/md';

const AddRevenueOfficerModal = ({ isOpen, onClose, showPopup, fetchData, API_BASE_URL, mmdas, zones, groups }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    email: '',
    mmda_id: '',
    zone_id: '',
    group_id: '',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]{10,}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const user = JSON.parse(localStorage.getItem('user'));

      const officerData = {
        first_name: formData.first_name.trim(),
        middle_name: formData.middle_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        mmda_id: formData.mmda_id ? parseInt(formData.mmda_id) : null,
        zone_id: formData.zone_id ? parseInt(formData.zone_id) : null,
        group_id: formData.group_id ? parseInt(formData.group_id) : null,
        status: formData.status,
        created_by: user?.user_id || null
      };

      const response = await fetch(`${API_BASE_URL}/revenue-officers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(officerData),
      });

      if (!response.ok) {
        throw new Error('Failed to create revenue officer');
      }

      showPopup('Revenue officer added successfully!', 'success');
      fetchData();
      handleClose();
    } catch (error) {
      console.error('Failed to add revenue officer:', error);
      showPopup('Failed to add revenue officer. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      phone: '',
      email: '',
      mmda_id: '',
      zone_id: '',
      group_id: '',
      status: 'active'
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }} onClick={handleClose}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: 16,
        padding: 0,
        width: '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        transform: 'scale(1)',
        transition: 'all 0.3s ease'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '24px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdPerson size={28} />
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: '600' }}>Add Revenue Officer</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <MdClose size={20} color="white" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* First Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.first_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.first_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.first_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.first_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.first_name}
                </p>
              )}
            </div>

            {/* Last Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.last_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.last_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.last_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.last_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.last_name}
                </p>
              )}
            </div>

            {/* Middle Name Field */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Middle Name (Optional)
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
                placeholder="Enter middle name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              />
            </div>

            {/* Phone Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPhone size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.phone ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.phone) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.phone) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.phone && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdEmail size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Email (Optional)
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.email ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.email) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.email) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.email && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* MMDA Selection */}
            {mmdas && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: 14
                }}>
                  <MdBusiness size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  MMDA
                </label>
                <select
                  name="mmda_id"
                  value={formData.mmda_id}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer'
                  }}
                  onFocus={e => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                >
                  <option value="">Select MMDA (Optional)</option>
                  {mmdas.map(mmda => (
                    <option key={mmda.mmda_id} value={mmda.mmda_id}>
                      {mmda.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Zone Selection */}
            {zones && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: 14
                }}>
                  <MdLocationOn size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Zone
                </label>
                <select
                  name="zone_id"
                  value={formData.zone_id}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer'
                  }}
                  onFocus={e => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                >
                  <option value="">Select Zone (Optional)</option>
                  {zones.map(zone => (
                    <option key={zone.zone_id} value={zone.zone_id}>
                      {zone.zone_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Group Selection */}
            {groups && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: 14
                }}>
                  Group
                </label>
                <select
                  name="group_id"
                  value={formData.group_id}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer'
                  }}
                  onFocus={e => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                >
                  <option value="">Select Group (Optional)</option>
                  {groups.map(group => (
                    <option key={group.group_id} value={group.group_id}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                backgroundColor: '#ffffff',
                color: '#374151',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: 8,
                background: isSubmitting
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Officer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRevenueOfficerModal;